import * as React from 'react';
import type { Id } from '@/convex';
import { ApiClientError, requestJson } from '@/lib/client-http';
import type { RepoItem } from '@/lib/github-types';

export type InstallationAccountType = 'User' | 'Organization';
export type InstallationRepoSelection = 'all' | 'selected';

export interface InstallationSummary {
  id: number;
  accountLogin: string;
  accountType: InstallationAccountType;
  repositorySelection: InstallationRepoSelection;
}

export function useGitHubRepos(orgId: Id<'orgs'> | undefined): {
  repos: RepoItem[];
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  needsBroaderAccess: boolean;
  installations: InstallationSummary[];
} {
  const [repos, setRepos] = React.useState<RepoItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [needsBroaderAccess, setNeedsBroaderAccess] = React.useState(false);
  const [installations, setInstallations] = React.useState<
    InstallationSummary[]
  >([]);

  React.useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setErrorCode(null);
        setNeedsBroaderAccess(false);
        setInstallations([]);
        const data = await requestJson<{
          repos?: RepoItem[];
          needsBroaderAccess?: boolean;
          installations?: InstallationSummary[];
        }>(
          `/api/github/repos?orgId=${orgId}`,
          undefined,
          'Failed to load repos',
        );
        if (!cancelled) {
          setRepos(data.repos ?? []);
          setNeedsBroaderAccess(Boolean(data.needsBroaderAccess));
          setInstallations(data.installations ?? []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load repositories',
          );
          setErrorCode(err instanceof ApiClientError ? err.code : null);
          setNeedsBroaderAccess(false);
          setInstallations([]);
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return {
    repos,
    loading,
    error,
    errorCode,
    needsBroaderAccess,
    installations,
  };
}
